import { useState, useEffect } from 'react';
import { message } from 'antd';
import { flowService } from '../services/flowService';
import { Flow, FlowConversation, FlowAction, FlowState, FlowMessage, FlowStatus } from '../types';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-assistants');

export const useFlows = (chatbotId: string) => {
  const [flowData, setFlowData] = useState<Flow[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
  const [conversationData, setConversationData] = useState<FlowConversation>({ messages: [] });
  const [actionsData, setActionsData] = useState<{ [key: string]: FlowAction }>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchFlows = async () => {
    try {
      setIsLoading(true);
      const flows = await flowService.fetchFlows(chatbotId);
      setFlowData(flows);
    } catch (error) {

    } finally {
      setIsLoading(false);
    }
  };

  const fetchActionsData = async (messages: FlowMessage[]) => {
    try {
      const actionIds = messages.filter((msg) => msg.actionId).map((msg) => msg.actionId);
      if (actionIds.length === 0) return;

      const actionsMap: { [key: string]: FlowAction } = {};
      for (const actionId of actionIds) {
        try {
          const action = await flowService.fetchAction(actionId);
          if (action) {
            actionsMap[actionId] = action;
          }
        } catch (error) {
          logger.error(`Error fetching action ${actionId}:`, error);
        }
      }
      setActionsData(actionsMap);
    } catch (error) {
      logger.error('Error fetching actions data:', error);
    }
  };

  const handleFlowClick = async (flow: Flow) => {
    try {
      const conversation = await flowService.fetchConversation(flow.id);
      setSelectedFlow(flow);
      setConversationData(conversation);
      await fetchActionsData(conversation.messages);
    } catch (error) {
      logger.error('Error fetching conversation:', error);
      message.warning('Could not load conversation messages');
      setSelectedFlow(flow);
      setConversationData({ messages: [] });
    }
  };

  const handleDeleteFlow = async (flow: Flow) => {
    try {
      await flowService.deleteFlow(flow.id);
      message.success(`Flow "${flow.name}" deleted successfully`);
      setFlowData((prev) => prev.filter((f) => f.id !== flow.id));
    } catch (error) {
      logger.error('Error deleting flow:', error);
      message.error('Error deleting flow');
    }
  };

  const handleDuplicateFlow = async (flow: Flow) => {
    try {
      await flowService.duplicateFlow(flow.id, chatbotId, flow);
      message.success(`Flow "${flow.name} (Copy)" duplicated successfully`);
      fetchFlows();
    } catch (error) {
      logger.error('Error duplicating flow:', error);
      message.error('Error duplicating flow');
    }
  };

  const handleUpdateFlowStatus = async (flow: Flow, status: FlowStatus, chatbotId: string) => {
    try {
      await flowService.updateFlowStatus(flow.id, status);
      message.success(`Flow status updated successfully`);
      fetchFlows();
      await flowService.updateFlowPrompt(chatbotId);
    } catch (error) {
      logger.error('Error updating flow status:', error);
      message.error('Error updating flow status');
    }
  };

  useEffect(() => {
    if (chatbotId) {
      fetchFlows();
    }
  }, [chatbotId]);

  return {
    flowData,
    selectedFlow,
    conversationData,
    actionsData,
    isLoading,
    handleFlowClick,
    handleDeleteFlow,
    handleDuplicateFlow,
    handleUpdateFlowStatus,
    fetchFlows
  };
}; 